import { useCallback, useEffect, useMemo, useState } from 'react';
import { useFormContext } from 'react-hook-form';
import {
    IconName,
    Stack,
    StackItem,
    Table,
    TableActiveColumnFilters,
    ActionToolbarButton,
    ActionToolbar,
    TableProvider,
    Typography,
    useRowSelection,
    useTableState,
} from '@optimus-web/core';
import { lt, useToggle } from '@mews/framework';
import { PortfolioEnterpriseRelationshipFilters, PortfolioStringFilter } from '@mews-commander/data-access';
import { UnicodeEntity } from '@mews/enums';
import { getBasePriceFieldName } from '../../../forms/schemas/configureProductFormSchema';
import { useProperties } from '../../../../hooks/useProperties';
import { PriceBulkActions } from '../../common/PriceBulkActions';
import { PricingTableData } from '../../../../types';
import { getColumns } from './columns';

interface Props {
    tableData: PricingTableData[];
    isFetching: boolean;
}

export const PricingMultiplePropertiesForm = ({ tableData, isFetching }: Props) => {
    const {
        clearErrors,
        formState: { isSubmitting, isValid },
        setValue,
    } = useFormContext();

    const { rowSelection, setRowSelection, selectedRowsIds, hasRowsSelected, resetRowSelection } = useRowSelection();

    const [basePriceBulkValue, setBasePriceBulkValue] = useState<number | undefined>();
    const [isBulkValueApplied, setIsBulkValueApplied] = useToggle();
    const [tableState, setTableState] = useTableState<
        PricingTableData,
        PortfolioEnterpriseRelationshipFilters & { defaultCurrencyCode: Array<PortfolioStringFilter> }
    >({
        pagination: {
            show: false,
        },
    });

    const { filteredData, selectedCountries, selectedCurrencies } = useProperties({
        data: tableData,
        tableState,
    });

    useEffect(() => {
        if (!isSubmitting) return;

        if (isValid) return;

        // Form submission failed due to validation errors, reset filters to show all inputs in the tables.
        setTableState((state) => ({
            ...state,
            filters: {
                countryName: [],
                defaultCurrencyCode: [],
            },
        }));
    }, [isSubmitting, isValid, setTableState]);

    const columns = useMemo(
        () => getColumns(selectedCountries, selectedCurrencies),
        // Re-render when a bulk value is applied to update the values of the input fields.
        // eslint-disable-next-line react-hooks/exhaustive-deps
        [filteredData, selectedCountries, selectedCurrencies, isBulkValueApplied]
    );

    const handleOnClick = useCallback(() => {
        selectedRowsIds.forEach((id) => {
            const fieldName = getBasePriceFieldName(id);
            setValue(fieldName, basePriceBulkValue, { shouldDirty: true, shouldValidate: true });
        });

        setIsBulkValueApplied();
    }, [selectedRowsIds, basePriceBulkValue, setValue, setIsBulkValueApplied]);

    const handleResetSelection = useCallback(() => {
        const names = selectedRowsIds.map((id) => getBasePriceFieldName(id));
        clearErrors(names);
        resetRowSelection();
    }, [clearErrors, resetRowSelection, selectedRowsIds]);

    return (
        <Stack vertical spacing="300">
            <Stack vertical spacing="200">
                <Typography textStyle="titleSmallStrong" as="div">
                    {lt('BasePrice')}
                </Typography>
                <Typography textStyle="bodyMedium" variant="secondary" as="div">
                    {lt('ConfigureProductPricingBody')}
                </Typography>
            </Stack>
            <Stack>
                <Typography textStyle="bodyLargeStrong" as="div">
                    {lt('ConfigureProductPricingSubHeading', { count: tableData.length })}
                </Typography>
                {UnicodeEntity.NoBreakSpace}
                <Typography textStyle="bodyLargeStrong" variant="tertiary" as="div">
                    {tableData.length}
                </Typography>
            </Stack>
            <TableProvider columns={columns} state={tableState} onStateChange={setTableState} as="div">
                <TableActiveColumnFilters />

                <ActionToolbar
                    disabled={!hasRowsSelected}
                    leftSide={
                        <Stack itemAlignment="center" spacing="200">
                            <StackItem>{lt('SelectedItems', { count: selectedRowsIds.length })}</StackItem>
                            {hasRowsSelected && (
                                <StackItem>
                                    <ActionToolbarButton icon={IconName.ClearSelection} onClick={handleResetSelection}>
                                        {lt('ClearSelection')}
                                    </ActionToolbarButton>
                                </StackItem>
                            )}
                        </Stack>
                    }
                    rightSide={
                        <>
                            <PriceBulkActions
                                basePriceBulkValue={basePriceBulkValue}
                                handleOnClick={handleOnClick}
                                hasRowsSelected={hasRowsSelected}
                                setBasePriceBulkValue={setBasePriceBulkValue}
                            />
                        </>
                    }
                />

                <Table
                    uniqueRowId="id"
                    fixedFirstColumn
                    data={filteredData}
                    amountOfRows={tableData.length}
                    loading={isFetching}
                    rowSelection={{
                        rowSelection,
                        onRowSelectionChange: setRowSelection,
                    }}
                />
            </TableProvider>
        </Stack>
    );
};
